import { forwardRef, useId, type ButtonHTMLAttributes, type CSSProperties, type InputHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { classNames } from '../../lib/format';
import { useReveal } from '../../hooks/useReveal';

type Variant = 'primary' | 'accent' | 'outline' | 'ghost';

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; loading?: boolean; full?: boolean }
>(({ variant = 'primary', loading, full, className, children, disabled, ...rest }, ref) => (
  <button
    ref={ref}
    className={classNames('btn', `btn-${variant}`, full && 'w-full', className)}
    disabled={disabled || loading}
    {...rest}
  >
    {loading && <Loader2 size={16} className="animate-spin" />}
    {children}
  </button>
));
Button.displayName = 'Button';

export function LinkButton({
  to,
  variant = 'primary',
  className,
  children,
}: {
  to: string;
  variant?: Variant;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link to={to} className={classNames('btn', `btn-${variant}`, className)}>
      {children}
    </Link>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { label?: string; error?: string }>(
  ({ label, error, id, className, ...rest }, ref) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    return (
      <div>
        {label && (
          <label htmlFor={inputId} className="label">
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          className={classNames('field', error && 'border-warm-orange', className)}
          {...rest}
        />
        {error && <p className="mt-1 text-[0.75rem] text-warm-orange">{error}</p>}
      </div>
    );
  },
);
Input.displayName = 'Input';

export function Badge({ children, tone = 'teal' }: { children: ReactNode; tone?: 'teal' | 'orange' | 'grey' | 'aqua' }) {
  const styles: Record<string, string> = {
    teal: 'bg-pale-aqua text-deep-teal',
    orange: 'bg-[#FCE7D9] text-warm-orange',
    grey: 'bg-black/[0.05] text-secondary-grey',
    aqua: 'bg-mesh-blue/15 text-deep-teal',
  };
  return <span className={classNames('badge', styles[tone])}>{children}</span>;
}

export function Price({ amount, compareAt, currency = 'INR', className }: { amount: number; compareAt?: number | null; currency?: string; className?: string }) {
  const fmt = (v: number) =>
    // whole rupees without ".00", anything else with both paise digits ("₹145.90", never "₹145.9")
    new Intl.NumberFormat('en-IN', { style: 'currency', currency, minimumFractionDigits: v % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 }).format(v / 100);
  return (
    <span className={classNames('inline-flex items-baseline gap-2', className)}>
      <span className="font-semibold text-charcoal">{fmt(amount)}</span>
      {compareAt != null && compareAt > amount && (
        <span className="text-sm text-secondary-grey line-through">{fmt(compareAt)}</span>
      )}
    </span>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={classNames('skeleton rounded-lg', className)} />;
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-secondary-grey" role="status">
      <Loader2 className="animate-spin" size={20} />
      <span className="text-sm">{label}...</span>
    </div>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="card flex flex-col items-center gap-3 px-6 py-14 text-center">
      <h3 className="text-lg font-semibold">{title}</h3>
      {hint && <p className="max-w-md text-sm text-secondary-grey">{hint}</p>}
      {action}
    </div>
  );
}

export function Section({
  title,
  eyebrow,
  subtitle,
  children,
  action,
  className,
}: {
  title?: string;
  eyebrow?: string;
  subtitle?: string;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <section className={classNames('py-5 sm:py-6', className)}>
      <div className="container-ms">
        {(title || action) && (
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              {eyebrow && (
                <span className="mb-1.5 block text-[0.72rem] font-bold uppercase tracking-[0.16em] text-logo-to">
                  {eyebrow}
                </span>
              )}
              {title && <h2 className="text-2xl font-semibold sm:text-3xl">{title}</h2>}
              {subtitle && <p className="mt-1 text-sm text-secondary-grey">{subtitle}</p>}
            </div>
            {action}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

/**
 * Fades + rises an element in once it scrolls into view. Purely a progressive
 * enhancement - see useReveal for the no-JS/old-browser fallback, and
 * global.css neutralises the transition under prefers-reduced-motion.
 */
export function Reveal({
  children,
  delay = 0,
  className,
  as: As = 'div',
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: 'div' | 'li';
}) {
  const { ref, visible } = useReveal<HTMLDivElement>();
  const style: CSSProperties = delay ? { transitionDelay: `${delay}ms` } : {};
  return (
    <As ref={ref as never} className={classNames('reveal', visible && 'reveal-visible', className)} style={style}>
      {children}
    </As>
  );
}
